/* ==========================================================================
   BuildDirect — Header
   --------------------------------------------------------------------------
   Four jobs, all of them measured in the header redesign brief:

   1. Favorites. 63% of sessions are mobile at $142 AOV; desktop is $614.
      Mobile researches, desktop buys, and nothing in the header carried the
      handoff. Favorites persist in localStorage (no account required) and
      "email me this list" mails the floors themselves, so the list opens on
      the device where the pallet gets bought.

   2. Mobile full-screen search. Mobile search ran at 3.5% against a catalog
      people shop by species, because the box was an icon. Opening full-screen
      with recent searches and species chips is the fix.

   3. Two levels of interaction on mobile. Tap a pillar, see everything under it:
      the group level is flattened open rather than being a second tap.

   4. Nav instrumentation. GA4 records only generic click events today, so
      header CTR by pillar cannot be measured. Every header link push a
      header_nav_click with link_text and link_url before anything else ships,
      so Phase 1 has a before-and-after.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__bdHeaderInit) return;
  window.__bdHeaderInit = true;

  /* The copy is US English; two names deliberately are not. This key already
     holds saved lists in shoppers' browsers and the favourites_* dataLayer
     events below are what GA4 reports on, so renaming either would silently
     empty one and break the other. They stay as spelled where they are data
     contracts rather than something anyone reads. */
  var FAVORITES_KEY = 'bd_favourites';
  var RECENT_SEARCHES_KEY = 'bd_recent_searches';
  var MAX_FAVORITES = 60;
  var MAX_RECENT_SEARCHES = 6;

  /* ------------------------------------------------------------- helpers */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function readStore(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (e) {
      /* A corrupt or unavailable store must never break the header. */
      return [];
    }
  }

  function writeStore(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      /* Private browsing / quota. Counter stays correct for this page view. */
      return false;
    }
  }

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    for (var k in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
    }
    window.dataLayer.push(data);
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  /* Writes a count into every matching bubble, hiding it at zero. */
  function paintCount(selector, count) {
    $$(selector).forEach(function (el) {
      var number = $('[data-bd-count-number]', el) || el;
      number.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count === 0;
    });
  }

  /* The bubble is decorative; the count is announced from a live region. */
  function announceCount(selector, template, count) {
    var target = $(selector);
    if (!target || !template) return;
    target.textContent = template.replace('__count__', String(count));
  }

  function string(key) {
    return (window.bdHeader && window.bdHeader.strings && window.bdHeader.strings[key]) || '';
  }

  /* ---------------------------------------------------------- favorites */

  var favorites = {
    all: function () {
      return readStore(FAVORITES_KEY);
    },

    count: function () {
      return this.all().length;
    },

    has: function (id) {
      var items = this.all();
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].id) === String(id)) return true;
      }
      return false;
    },

    /* An item carries enough of itself to render the panel with no fetch:
       { id, title, url, image, price, variant }. */
    add: function (item) {
      if (!item || !item.id) return false;
      var items = this.all();
      if (this.has(item.id)) return false;
      items.unshift(item);
      if (items.length > MAX_FAVORITES) items = items.slice(0, MAX_FAVORITES);
      writeStore(FAVORITES_KEY, items);
      this.changed('add', item);
      return true;
    },

    remove: function (id) {
      var items = this.all();
      var kept = items.filter(function (item) {
        return String(item.id) !== String(id);
      });
      if (kept.length === items.length) return false;
      writeStore(FAVORITES_KEY, kept);
      this.changed('remove', { id: id });
      return true;
    },

    toggle: function (item) {
      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }
      this.add(item);
      return true;
    },

    clear: function () {
      writeStore(FAVORITES_KEY, []);
      this.changed('clear', {});
    },

    changed: function (action, item) {
      var count = this.count();
      paintCount('[data-bd-favorites-count]', count);
      announceCount('[data-bd-favorites-status]', string('favoritesCount'), count);
      syncFavoriteButtons();
      renderFavoritesPanel();
      push('favourites_change', {
        favourites_action: action,
        favourites_count: count,
        item_id: item && item.id ? String(item.id) : ''
      });
      emit('on:favorites:change', { action: action, count: count, items: this.all() });
    },

    /* The list as plain text, one floor per line with an absolute link. This is
       the bridge: it opens on the desktop where the pallet actually gets bought,
       and needs no account, backend or integration to work. */
    asText: function () {
      return this.all()
        .map(function (item) {
          var url = safeUrl(item.url);
          return '• ' + (item.title || '') + (url ? '\n  ' + window.location.origin + url : '');
        })
        .join('\n\n');
    }
  };

  window.bdFavorites = favorites;

  /* Reflects saved state onto any [data-bd-favorite] toggle on the page. */
  function syncFavoriteButtons() {
    $$('[data-bd-favorite]').forEach(function (btn) {
      var id = btn.getAttribute('data-bd-favorite');
      var saved = favorites.has(id);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      btn.classList.toggle('is-saved', saved);
      var label = btn.getAttribute(saved ? 'data-bd-label-saved' : 'data-bd-label-save');
      if (label) {
        var text = $('[data-bd-favorite-text]', btn);
        if (text) text.textContent = label;
        else btn.setAttribute('aria-label', label);
      }
    });
  }

  /* Click delegation so buttons rendered later (quick view, infinite scroll)
     work without re-binding. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bd-favorite]');
    if (!btn) return;
    e.preventDefault();

    favorites.toggle({
      id: btn.getAttribute('data-bd-favorite'),
      title: btn.getAttribute('data-bd-favorite-title') || '',
      url: btn.getAttribute('data-bd-favorite-url') || '',
      image: btn.getAttribute('data-bd-favorite-image') || '',
      price: btn.getAttribute('data-bd-favorite-price') || '',
      variant: btn.getAttribute('data-bd-favorite-variant') || ''
    });
  });

  /* The PDP swaps variants in place (on:variant:change) instead of re-rendering
     its section, so a heart rendered for the 5in board would otherwise keep
     saving the 5in board after the shopper picked the 7in. Rebind it from the
     server-rendered variant map, which already carries Liquid-formatted prices.
     Only buttons that opt in move; collection cards stay pinned to their SKU. */
  document.addEventListener('on:variant:change', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant || !variant.id) return;

    var scope = (e.target && e.target.closest && e.target.closest('.shopify-section')) || document;
    var wrap = scope.querySelector('[data-bd-favorite-wrap]');
    if (!wrap) return;

    var btn = wrap.querySelector('[data-bd-favorite][data-bd-favorite-follows-variant]');
    var json = wrap.querySelector('[data-bd-favorite-variants]');
    if (!btn || !json) return;

    var map;
    try {
      map = JSON.parse(json.textContent);
    } catch (err) {
      /* A malformed map must not take the add-to-cart button down with it. */
      return;
    }

    var next = map && map[String(variant.id)];
    if (!next) return;

    btn.setAttribute('data-bd-favorite', next.key);
    btn.setAttribute('data-bd-favorite-title', next.title);
    btn.setAttribute('data-bd-favorite-url', next.url);
    btn.setAttribute('data-bd-favorite-image', next.image);
    btn.setAttribute('data-bd-favorite-price', next.price);
    btn.setAttribute('data-bd-favorite-variant', next.variant);

    /* The new SKU may already be saved, so repaint pressed state from the store
       rather than leaving the previous variant's. */
    syncFavoriteButtons();
  });

  /* ----------------------------------------------------- favorites panel */

  function renderFavoritesPanel() {
    var panel = $('[data-bd-favorites-panel]');
    if (!panel) return;

    var list = $('[data-bd-favorites-list]', panel);
    var emptyState = $('[data-bd-favorites-empty]', panel);
    var actions = $('[data-bd-favorites-actions]', panel);
    var items = favorites.all();

    if (!list) return;

    if (!items.length) {
      list.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (actions) actions.hidden = true;
      return;
    }

    if (emptyState) emptyState.hidden = true;
    if (actions) actions.hidden = false;

    list.innerHTML = items
      .map(function (item) {
        var imageUrl = safeImageUrl(item.image);
        var image = imageUrl
          ? '<img src="' + escapeAttr(imageUrl) + '" alt="" loading="lazy" width="56" height="56">'
          : '';
        return (
          '<li class="bd-favorites__item">' +
          '<a class="bd-favorites__link" href="' + escapeAttr(safeUrl(item.url)) + '">' +
          '<span class="bd-favorites__media">' + image + '</span>' +
          '<span class="bd-favorites__info">' +
          '<span class="bd-favorites__title">' + escapeHtml(item.title) + '</span>' +
          (item.price ? '<span class="bd-favorites__price">' + escapeHtml(item.price) + '</span>' : '') +
          '</span></a>' +
          '<button type="button" class="bd-favorites__remove" data-bd-favorite-remove="' +
          escapeAttr(item.id) + '" aria-label="' + escapeAttr(removeLabel(item.title)) + '">&times;</button>' +
          '</li>'
        );
      })
      .join('');
  }

  function removeLabel(title) {
    var template = string('removeFavorite') || 'Remove __title__';
    return template.replace('__title__', title || '');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  /* Favorites are read back out of localStorage and written into href and src,
     so only same-origin paths are accepted. Anything else — a javascript: or
     data: URL planted by another script — is dropped rather than rendered. */
  function safeUrl(value) {
    var url = String(value == null ? '' : value).trim();
    if (!url) return '';
    if (url.charAt(0) === '/' && url.charAt(1) !== '/') return url;
    if (url.indexOf(window.location.origin + '/') === 0) {
      return url.slice(window.location.origin.length);
    }
    return '';
  }

  /* Product imagery is served from the Shopify CDN, so images get the same
     treatment with that one host added. */
  function safeImageUrl(value) {
    var url = String(value == null ? '' : value).trim();
    if (!url) return '';
    if (/^https:\/\/[a-z0-9-]+\.shopifycdn\.com\//i.test(url)) return url;
    if (/^https:\/\/cdn\.shopify\.com\//i.test(url)) return url;
    if (/^\/\/cdn\.shopify\.com\//i.test(url)) return url;
    return safeUrl(url);
  }

  function closeFavoritesPanel() {
    var panel = $('[data-bd-favorites-panel]');
    var toggle = $('[data-bd-favorites-toggle]');
    if (panel) panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-bd-favorites-toggle]');
    var panel = $('[data-bd-favorites-panel]');

    if (toggle) {
      e.preventDefault();
      if (!panel) return;
      var willOpen = panel.hidden;
      panel.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) push('favourites_open', { favourites_count: favorites.count() });
      return;
    }

    /* Clicking anywhere outside closes it, the way the cart drawer behaves. */
    if (panel && !panel.hidden && !e.target.closest('[data-bd-favorites-panel]')) {
      closeFavoritesPanel();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var panel = $('[data-bd-favorites-panel]');
    if (panel && !panel.hidden) {
      closeFavoritesPanel();
      var toggle = $('[data-bd-favorites-toggle]');
      if (toggle) toggle.focus();
    }
  });

  document.addEventListener('click', function (e) {
    var remove = e.target.closest && e.target.closest('[data-bd-favorite-remove]');
    if (remove) {
      e.preventDefault();
      favorites.remove(remove.getAttribute('data-bd-favorite-remove'));
      return;
    }

    /* "Email me this list" with no backend: a mailto carrying the floors
       themselves, so the list survives the hop from phone to desktop. */
    var share = e.target.closest && e.target.closest('[data-bd-favorites-share]');
    if (share) {
      e.preventDefault();
      if (!favorites.count()) return;

      var subject = string('shareSubject') || 'My BuildDirect favorites';
      var body = (string('shareBody') || 'Here are the floors I saved:') + '\n\n' + favorites.asText();

      push('favourites_share', { favourites_count: favorites.count(), share_method: 'email' });
      window.location.href =
        'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    }
  });

  /* ------------------------------------------------- mobile full-screen search */

  var search = {
    overlay: null,
    input: null,
    lastFocused: null,

    init: function () {
      this.overlay = $('[data-bd-search-overlay]');
      if (!this.overlay) return;
      this.input = $('.js-search-input', this.overlay);

      $$('[data-bd-search-open]').forEach(
        function (btn) {
          btn.addEventListener('click', this.open.bind(this));
        }.bind(this)
      );

      $$('[data-bd-search-close]', this.overlay).forEach(
        function (btn) {
          btn.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      this.overlay.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'Escape') this.close(e);
        }.bind(this)
      );

      this.renderRecent();
    },

    open: function (e) {
      if (e) e.preventDefault();
      this.lastFocused = document.activeElement;
      this.overlay.hidden = false;
      /* Let the browser paint before focusing, or iOS skips the keyboard. */
      requestAnimationFrame(
        function () {
          this.overlay.classList.add('is-open');
          document.body.classList.add('bd-search-open');
          if (this.input) this.input.focus();
        }.bind(this)
      );
      push('search_open', { search_surface: 'mobile_fullscreen' });
    },

    close: function (e) {
      if (e) e.preventDefault();
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('bd-search-open');
      /* Match the CSS transition before removing from the a11y tree. */
      setTimeout(
        function () {
          this.overlay.hidden = true;
        }.bind(this),
        200
      );
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    },

    /* Recent searches are the cheapest re-entry for a pro re-ordering a SKU. */
    renderRecent: function () {
      var wrapper = $('[data-bd-recent-searches]');
      if (!wrapper) return;

      var terms = readStore(RECENT_SEARCHES_KEY);
      var list = $('[data-bd-recent-list]', wrapper);
      if (!list) return;

      if (!terms.length) {
        wrapper.hidden = true;
        return;
      }

      wrapper.hidden = false;
      list.innerHTML = terms
        .map(function (term) {
          return (
            '<li><a class="bd-chip" href="/search?q=' +
            encodeURIComponent(term) +
            '" data-bd-nav-kind="recent-search">' +
            escapeHtml(term) +
            '</a></li>'
          );
        })
        .join('');
    }
  };

  function rememberSearch(term) {
    term = (term || '').trim();
    if (term.length < 2) return;

    var terms = readStore(RECENT_SEARCHES_KEY).filter(function (existing) {
      return existing.toLowerCase() !== term.toLowerCase();
    });
    terms.unshift(term);
    writeStore(RECENT_SEARCHES_KEY, terms.slice(0, MAX_RECENT_SEARCHES));
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form.search')) return;
    var input = $('.js-search-input', form);
    if (input) rememberSearch(input.value);
  });

  /* ------------------------------------------------- two-level mobile accordion */

  /* Two levels of interaction: tap a pillar, see everything under it.
     
     The menu is pillar -> group -> links ("Wood Flooring" -> "Shop by Category"
     -> the categories), so the group is flattened open rather than being a second
     tap. An earlier version treated that level as "the third level" and hid it,
     which hid the category links themselves and left the drawer empty below the
     pillar. Flatten, never hide.
     
     Runs after the theme's NavMenu, which closes nested disclosures on mobile, so
     it re-applies on breakpoint change and when a pillar is opened. */
  function flattenGroups() {
    var mobile = !window.matchMedia('(min-width: 769px)').matches;
    $$('.main-menu .main-nav__grandchild').forEach(function (list) {
      var details = list.closest('details');
      if (!details) return;
      var summary = details.querySelector('summary');
      if (!summary) return;

      if (mobile) {
        details.open = true;
        summary.setAttribute('data-bd-flattened', 'true');
      } else {
        summary.removeAttribute('data-bd-flattened');
      }
    });
  }

  /* Opening a pillar reveals groups the theme may have just closed. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest('.main-menu summary, .main-menu__toggle')) return;
    /* After the theme's own toggle handler has run. */
    setTimeout(flattenGroups, 0);
  });

  /* ------------------------------------------------------- instrumentation */

  /* GA4 cannot report header CTR by pillar today. link_text and link_url turn
     the header into something measurable, which §08 wants in place first. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.header a[href], .main-menu a[href]');
    if (!link) return;

    /* Must be the pillar's own li, not the nearest one — a subcategory link sits
       inside its own li several levels down, which carries no pillar link. */
    var pillar = link.closest('.main-nav > li');
    var pillarLink = pillar ? pillar.querySelector('.main-nav__item--primary') : null;
    var isPrimary = link.classList.contains('main-nav__item--primary');

    var level = 'utility';
    if (isPrimary) level = 'pillar';
    else if (link.classList.contains('child-nav__item')) level = 'subcategory';
    else if (link.classList.contains('grandchild-nav__item')) level = 'leaf';
    else if (link.classList.contains('bd-nav-column__item') || link.classList.contains('bd-chip')) {
      level = 'attribute';
    } else if (
      link.classList.contains('bd-nav-feature__card') ||
      link.classList.contains('bd-nav-feature__link')
    ) {
      /* Column 4 is merchandised per pillar, so it only pays for itself if its
         clicks are separable from the utility bucket they would otherwise land
         in. Same reason nav_pillar exists. */
      level = 'feature';
    }

    push('header_nav_click', {
      link_text: (link.textContent || '').replace(/\s+/g, ' ').trim(),
      link_url: link.getAttribute('href'),
      nav_level: level,
      nav_kind: link.getAttribute('data-bd-nav-kind') || level,
      nav_pillar: pillarLink ? pillarLink.textContent.replace(/\s+/g, ' ').trim() : '',
      nav_viewport: window.matchMedia('(min-width: 769px)').matches ? 'desktop' : 'mobile'
    });
  });

  /* ---------------------------------------------------------------- start */

  function init() {
    paintCount('[data-bd-favorites-count]', favorites.count());
    announceCount('[data-bd-favorites-status]', string('favoritesCount'), favorites.count());
    syncFavoriteButtons();
    renderFavoritesPanel();
    search.init();
    flattenGroups();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('on:breakpoint-change', flattenGroups);
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('store-header')) init();
  });
})();
